"use client";

import { usePayments } from "@/features/payments/hooks/usePayments";
import {
    applyDiscount,
    formatPrice,
    VAT_PERCENT,
} from "@/features/payments/lib/utils";
import { t } from "@/lib/localization";
import {
    billingTransactionTypes,
    pricing,
    type BillingTransactionType,
} from "@yedalabs/schema";

const labelKeys: Record<BillingTransactionType, string> = {
    storage: "settings.billing.add-funds.storage-tb",
    bandwidth: "settings.billing.add-funds.bandwidth-tb",
    credits: "settings.billing.add-funds.credits",
};

export default function PaymentSummary() {
    const addFundsPayload = usePayments((state) => state.addFundsPayload);

    if (!addFundsPayload) return null;

    const rows = billingTransactionTypes
        .map((type) => {
            const { amount, discount } = addFundsPayload[type];
            const basePrice = pricing[type] * amount;
            const discountedPrice = applyDiscount(basePrice, discount);
            return { type, amount, discount, basePrice, discountedPrice };
        })
        .filter((row) => row.amount > 0);

    const subtotal = rows.reduce((sum, row) => sum + row.discountedPrice, 0);
    const vatAmount = subtotal * (VAT_PERCENT / 100);
    const total = subtotal + vatAmount;

    return (
        <div className="flex flex-col gap-2">
            <dl className="text-xs">
                {rows.map((row) => (
                    <div
                        key={row.type}
                        className="flex items-center justify-between pb-1.5"
                    >
                        <dt>
                            {t(labelKeys[row.type], undefined, {
                                amount: row.amount,
                            })}
                        </dt>
                        <dd className="flex items-center gap-2">
                            {row.discount > 0 && (
                                <span className="text-slate-400 line-through">
                                    {formatPrice(row.basePrice)}
                                </span>
                            )}
                            <span>{formatPrice(row.discountedPrice)}</span>
                        </dd>
                    </div>
                ))}
                <div className="flex items-center justify-between mt-2 pb-1.5">
                    <dt>{t("payments.summary.subtotal")}</dt>
                    <dd>{formatPrice(subtotal)}</dd>
                </div>
                <div className="flex items-center justify-between pb-1.5">
                    <dt>
                        {t("payments.summary.vat", undefined, {
                            percentage: VAT_PERCENT,
                        })}
                    </dt>
                    <dd>{formatPrice(vatAmount)}</dd>
                </div>
                <div className="flex items-center justify-between text-sm mt-2">
                    <dt className="font-medium">
                        {t("payments.summary.total")}
                    </dt>
                    <dd className="font-medium">{formatPrice(total)}</dd>
                </div>
            </dl>
        </div>
    );
}
